import { useState, useEffect } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useAuth } from './useAuth';
import { logOut } from '../lib/auth';
import { isCapacitorApp } from '../utils/platformDetection';

// Desktop: 60 minute timeout with 2 minute warning
const DESKTOP_TIMEOUT = 60 * 60 * 1000;        // 60 minutes
const DESKTOP_PROMPT_BEFORE = 2 * 60 * 1000;   // 2 minutes warning

// Mobile: High timeout (effectively disabled, relying on device security + biometric login)
// Note: Can't be 0 because react-idle-timer requires promptBeforeIdle < timeout
const MOBILE_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours (effectively disabled)

export const useSessionTimeout = () => {
  const { user } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const isMobile = isCapacitorApp();

  const onIdle = async () => {
    setShowWarning(false);
    await logOut();
    localStorage.setItem('logoutReason', 'session-timeout');
    window.location.href = '/login';
  };

  const onPrompt = () => {
    setShowWarning(true);
  };

  const onActive = () => {
    setShowWarning(false);
  };

  const { activate, getRemainingTime } = useIdleTimer({
    onIdle,
    onPrompt,
    onActive,
    timeout: isMobile ? MOBILE_TIMEOUT : DESKTOP_TIMEOUT,
    promptBeforeIdle: DESKTOP_PROMPT_BEFORE, // Always use desktop prompt time
    throttle: 500,
    crossTab: true,
    syncTimers: 200,
    disabled: !user || isMobile, // Timer is disabled on mobile anyway
  });

  // Update remaining time for countdown display
  useEffect(() => {
    if (!showWarning || isMobile) return;

    const interval = setInterval(() => {
      const remaining = getRemainingTime();
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        setShowWarning(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning, getRemainingTime, isMobile]);

  const extendSession = () => {
    activate();
    setShowWarning(false);
  };

  return {
    showWarning: showWarning && !isMobile, // Never show on mobile
    timeRemaining,
    extendSession,
    isEnabled: !isMobile && !!user,
  };
};
